import { ordinalSuffix } from './shared_helpers'

export const cflTeamNameIDArrayMap = new Map([
    [5611, 'hamilton-tiger-cats'],
    [5612, 'montreal-alouettes'],
    [27755, 'ottawa-redblacks'],
    [5615, 'toronto-argonauts'],
    [5608, 'bc-lions'],
    [5609, 'calgary-stampeders'],
    [5610, 'edmonton-elks'],
    [5614, 'saskatchewan-roughriders'],
    [5616, 'winnipeg-blue-bombers'],
])

export function formatSeasonLeaders(homeTeam, visitingTeam) {
    const leaderCategories = [
        {
            title: 'Passing Yards',
            categoryKey: 'passing_leader',
            valueKey: 'yards',
        },
        {
            title: 'Rushing Yards',
            categoryKey: 'rushing_leader',
            valueKey: 'rushing_yards', // check if there in a rushing_yards value to handle cases where the QB is a passing leader as well as a rushing leader.
            alternateValueKey: 'yards',
        },
        {
            title: 'Receiving Yards',
            categoryKey: 'receiving_leader',
            valueKey: 'yards',
        },
        {
            title: 'Tackles',
            categoryKey: 'tackles_leader',
            valueKey: 'tackles',
        },
        {
            title: 'Sacks',
            categoryKey: 'sacks_leader',
            valueKey: 'sacks',
            decimalPlaces: 1,
        },
        {
            title: 'Interceptions',
            categoryKey: 'interceptions_leader',
            valueKey: 'interceptions',
        },
    ]

    const seasonLeaders = leaderCategories.map(leaderCategory => {
        const homeTeamData = homeTeam?.season_leaders?.[leaderCategory.categoryKey]
        const visitingTeamData = visitingTeam?.season_leaders?.[leaderCategory.categoryKey]

        if (!homeTeamData || !visitingTeamData) {
            return null
        }

        let homeTeamValue = homeTeamData[leaderCategory.valueKey]
        let visitingTeamValue = visitingTeamData[leaderCategory.valueKey]

        if (leaderCategory.alternateValueKey) {
            homeTeamValue = homeTeamValue || homeTeamData[leaderCategory.alternateValueKey]
            visitingTeamValue =
                visitingTeamValue || visitingTeamData[leaderCategory.alternateValueKey]
        }

        if (leaderCategory.decimalPlaces) {
            homeTeamValue = homeTeamValue.toFixed(leaderCategory.decimalPlaces)
            visitingTeamValue = visitingTeamValue.toFixed(leaderCategory.decimalPlaces)
        }

        return {
            title: leaderCategory.title,
            homeTeamShortName: homeTeam.short_name,
            homeTeamColor: homeTeam.color,
            visitingTeamColor: visitingTeam.color,
            visitingTeamShortName: visitingTeam.short_name,
            homeTeamPlayerImage: homeTeamData.image_urls.xs,
            visitingTeamPlayerImage: visitingTeamData.image_urls.xs,
            homeTeamLogo: homeTeam.image_url_90,
            visitingTeamLogo: visitingTeam.image_url_90,
            homeTeamValue: homeTeamValue,
            visitingTeamValue: visitingTeamValue,
            homeTeamPlayerName: `${homeTeamData.first_name.charAt(0)}. ${homeTeamData.last_name}`,
            visitingTeamPlayerName: `${visitingTeamData.first_name.charAt(0)}. ${
                visitingTeamData.last_name
            }`,
        }
    })
    return seasonLeaders.filter(n => n) // filter out null values
}

export function formatPassingBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'COMP', dataKey: 'passes_completed', bold: false, textAlign: 'center' },
            { title: 'ATT', dataKey: 'passes_attempted', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'TD', dataKey: 'touchdowns', bold: false, textAlign: 'center' },
            { title: 'INT', dataKey: 'interceptions', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'COMP', dataKey: 'passes_completed', bold: false, textAlign: 'center' },
            { title: 'ATT', dataKey: 'passes_attempted', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'TD', dataKey: 'touchdowns', bold: false, textAlign: 'center' },
            { title: 'INT', dataKey: 'interceptions', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerPassesData => {
                return {
                    ...playerPassesData,
                    player_name: formatPlayerName(playerPassesData),
                }
            })
            .sort((a, b) => b.yards - a.yards),
        homeTeamBoxscoreStats
            .map(playerPassesData => {
                return {
                    ...playerPassesData,
                    player_name: formatPlayerName(playerPassesData),
                }
            })
            .sort((a, b) => b.yards - a.yards),
    ]

    return { columns, data }
}

export function formatRushingBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'ATT', dataKey: 'rushes_attempted', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'TD', dataKey: 'touchdowns', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_rush', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'ATT', dataKey: 'rushes_attempted', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'TD', dataKey: 'touchdowns', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_rush', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: formatPlayerName(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),

        homeTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: formatPlayerName(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),
    ]

    return { columns, data }
}

export function formatReceivingBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'REC', dataKey: 'receptions', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'TD', dataKey: 'touchdowns', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_receiving', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'REC', dataKey: 'receptions', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'TD', dataKey: 'touchdowns', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_receiving', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: formatPlayerName(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),

        homeTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: formatPlayerName(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),
    ]

    return { columns, data }
}

export function formatKickingBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'FG', dataKey: 'made', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_kick', bold: false, textAlign: 'center' },
            { title: 'XP', dataKey: 'extra_points', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'FG', dataKey: 'made', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_kick', bold: false, textAlign: 'center' },
            { title: 'XP', dataKey: 'extra_points', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: formatPlayerName(playerData),
                    extra_points: formatExtraPoints(playerData),
                }
            })
            .sort((a, b) => b.made - a.made),

        homeTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: formatPlayerName(playerData),
                    extra_points: formatExtraPoints(playerData),
                }
            })
            .sort((a, b) => b.made - a.made),
    ]

    return { columns, data }
}

export function formatPuntingBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'P', dataKey: 'punts', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_punt', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'P', dataKey: 'punts', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_punt', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: formatPlayerName(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),

        homeTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: formatPlayerName(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),
    ]

    return { columns, data }
}

export function formatKickReturnsBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'KR', dataKey: 'returns', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_kick_return', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'KR', dataKey: 'returns', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_kick_return', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: formatPlayerName(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),

        homeTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: formatPlayerName(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),
    ]

    return { columns, data }
}

export function formatPuntReturnsBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'PR', dataKey: 'returns', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_punt_return', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'KR', dataKey: 'returns', bold: false, textAlign: 'center' },
            { title: 'YDS', dataKey: 'yards', bold: false, textAlign: 'center' },
            { title: 'AVG', dataKey: 'average', bold: false, textAlign: 'center' },
            { title: 'LNG', dataKey: 'longest_punt_return', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: formatPlayerName(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),

        homeTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: formatPlayerName(playerData),
                }
            })
            .sort((a, b) => b.yards - a.yards),
    ]

    return { columns, data }
}

export function formatDefenseBoxscore(
    homeTeamBoxscoreStats = [],
    visitingTeamBoxscoreStats = [],
    homeTeamTitleComponent,
    visitingTeamTitleComponent
) {
    const columns = [
        [
            {
                title: visitingTeamTitleComponent,
                dataKey: 'player_name',
                cellWidthOverrideMobile: 20,
            },
            { title: 'TCKL', dataKey: 'tackles', bold: false, textAlign: 'center' },
            { title: 'SACK', dataKey: 'sacks', bold: false, textAlign: 'center' },
            { title: 'INT', dataKey: 'interceptions', bold: false, textAlign: 'center' },
            { title: 'PDEF', dataKey: 'passes_defensed', bold: false, textAlign: 'center' },
        ],
        [
            { title: homeTeamTitleComponent, dataKey: 'player_name', cellWidthOverrideMobile: 20 },
            { title: 'TCKL', dataKey: 'tackles', bold: false, textAlign: 'center' },
            { title: 'SACK', dataKey: 'sacks', bold: false, textAlign: 'center' },
            { title: 'INT', dataKey: 'interceptions', bold: false, textAlign: 'center' },
            { title: 'PDEF', dataKey: 'passes_defensed', bold: false, textAlign: 'center' },
        ],
    ]

    const data = [
        visitingTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: formatPlayerName(playerData),
                }
            })
            .sort((a, b) => b.tackles - a.tackles),
        homeTeamBoxscoreStats
            .map(playerData => {
                return {
                    ...playerData,
                    player_name: formatPlayerName(playerData),
                }
            })
            .sort((a, b) => b.tackles - a.tackles),
    ]

    return { columns, data }
}

export function formatLastNineEvents(lastNineEvents = [], homeTeam, visitingTeam) {
    const massagedPlays = lastNineEvents.map(play => {
        const formattedPlayDescription = formatTeamYardsInText(play.description)
        const teamLogo =
            play.possession === homeTeam.short_name
                ? homeTeam.image_url_90
                : visitingTeam.image_url_90

        return {
            id: play.id,
            title: setPlayTitle(play),
            color: getPlayColor(play.play_type_id),
            bodyText: shortenPlayerNames(formattedPlayDescription, play.players),
            teamLogo: teamLogo,
        }
    })

    const ghostPlays = [
        { id: 1, isGhost: true },
        { id: 2, isGhost: true },
        { id: 3, isGhost: true },
        { id: 4, isGhost: true },
        { id: 5, isGhost: true },
        { id: 6, isGhost: true },
        { id: 7, isGhost: true },
        { id: 8, isGhost: true },
        { id: 9, isGhost: true },
    ]

    return massagedPlays.concat(ghostPlays.slice(massagedPlays.length))
}

function formatTeamYardsInText(originalText = '') {
    const teamYardageTexts = originalText.match(/[a-zA-Z]+[0-9]+/gm) || []
    if (!teamYardageTexts.length) {
        return originalText
    }

    teamYardageTexts.forEach(teamYardage => {
        const teamTricode = (teamYardage.match(/[a-zA-Z]+/g)[0] || '').toUpperCase()
        const teamYards = teamYardage.match(/[0-9]+/g)[0] || ''
        originalText = originalText.replace(teamYardage, `${teamTricode} ${teamYards}`)
    })

    return originalText
}

function setPlayTitle(play) {
    const yardlineTeamRegex = play.yardline.match(/[a-zA-Z]+/g) || []
    const yardLineDistanceRegex = play.yardline.match(/[0-9]+/g)
    const yardLineTeam = (yardlineTeamRegex[0] || '').toUpperCase()
    const yardLineDistance = yardLineDistanceRegex[0] || ''

    const playTypeText = play.play_type || ''

    switch (play.play_type_id) {
        case 5:
            return 'KICKOFF'
        case 6:
            return 'KICKOFF RETURN'
        case 10:
        case 11:
            return 'PENALTY'
        case 41:
            return 'ONSIDE KICKOFF'
        case 47:
            return 'BLOCKED EXTRA POINT'
        case 52:
            return 'TWO POINT CONVERSION'
        case 53:
        case 54:
        case 56:
            return 'MISSED TWO POINT CONVERSION'
        case 57:
        case 58:
            return 'TIMEOUT'
        case 22:
        case 55:
            return playTypeText.toUpperCase()
        default:
            return `${ordinalSuffix(play.down).toUpperCase()} & ${
                play.distance
            } / ${yardLineTeam} ${yardLineDistance}`
    }
}

function getPlayColor(playTypeId) {
    const darkBlue = '#0B223E'
    const red = '#DA291C'
    const grey = '#636A71'
    const brightBlue = '#0D5EB8'

    const playColorMap = new Map([
        [1, darkBlue],
        [2, darkBlue],
        [3, brightBlue],
        [4, darkBlue],
        [5, grey],
        [6, darkBlue],
        [7, grey],
        [8, darkBlue],
        [9, brightBlue],
        [10, red],
        [11, red],
        [12, ''],
        [13, ''],
        [14, brightBlue],
        [15, ''],
        [16, ''],
        [17, darkBlue],
        [18, darkBlue],
        [19, ''],
        [20, darkBlue],
        [21, ''],
        [22, darkBlue],
        [23, ''],
        [24, ''],
        [25, ''],
        [26, ''],
        [27, ''],
        [28, ''],
        [29, ''],
        [30, ''],
        [31, ''],
        [32, ''],
        [33, ''],
        [34, ''],
        [35, darkBlue],
        [36, ''],
        [37, ''],
        [38, ''],
        [39, ''],
        [40, ''],
        [41, grey],
        [42, darkBlue],
        [43, ''],
        [44, ''],
        [45, ''],
        [46, ''],
        [47, darkBlue],
        [48, ''],
        [49, ''],
        [50, ''],
        [51, ''],
        [52, darkBlue],
        [53, darkBlue],
        [54, darkBlue],
        [55, darkBlue],
        [56, darkBlue],
        [57, grey],
        [58, grey],
    ])
    return playColorMap.get(playTypeId) || grey
}

function shortenPlayerNames(originalText, players = []) {
    let newText = originalText

    if (!players.length) {
        return originalText
    }

    for (const player of players) {
        const textToReplace = `${player.first_name} ${player.last_name}`
        const playerInitial = player.first_name.charAt(0)
        const replacementText = `<b>${playerInitial}. ${player.last_name}</b>`

        newText = newText.replace(textToReplace, replacementText)
    }
    return newText
}

function formatExtraPoints(playerData) {
    if (playerData.extra_points_made && playerData.extra_points_attempted) {
        return `${playerData.extra_points_made}/${playerData.extra_points_attempted}`
    } else {
        return null
    }
}

export function formatPlayerName(player) {
    return `${player.first_name.charAt(0)}. ${player.last_name}`
}
